import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, timer } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationService } from './notification.service';
import {
  LoginRequest,
  AuthResponse,
  RefreshTokenRequest,
  AuthUser,
  UserInfo,
  ForgotPasswordRequest,
  ResetPasswordRequest
} from '../models/auth.models';
import { ConfigService } from './config.service';
import { Log, IShortLogger } from './logger.service';
import { LocalStorageService } from './local-storage.service';
import { NotificationsService } from './home-services/notifications.service';
import { UserPrefsService } from './user-prefs.service';
import { isManagementTeam } from '../utils/auth.utils';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly logger: IShortLogger<AuthService> = Log.create(AuthService);
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'auth_user';

  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private refreshTimer?: any;
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService,
    private localStorage: LocalStorageService,
    private notificationsService: NotificationsService,
    private userPrefsService: UserPrefsService,
    private notification: NotificationService
  ) {
    this.baseUrl = this.configService.getApiUrl();
    this.loadStoredAuth();
    this.setupAutoRefresh();
  }

  /**
   * Login with username and password
   */
  login(credentials: LoginRequest): Observable<AuthUser> {
    const url = `${this.baseUrl}/v1/auth/login`;

    return this.http.post<AuthResponse>(url, credentials).pipe(
      map(response => this.handleAuthSuccess(response)),
      catchError(this.handleError)
    );
  }

  /**
   * Logout the current user
   */
  logout(): Observable<any> {
    const url = `${this.baseUrl}/v1/auth/logout`;

    return this.http.post(url, {}).pipe(
      tap(() => this.clearAuth()),
      catchError(error => {
        // Even if logout fails on server, clear local auth
        this.clearAuth();
        return throwError(() => error);
      })
    );
  }

  /**
   * Refresh the access token
   */
  refreshToken(): Observable<AuthUser> {
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      this.clearAuth();
      throw new Error('No refresh token available');
    }

    const url = `${this.baseUrl}/v1/auth/refresh`;
    const request: RefreshTokenRequest = { refreshToken };

    return this.http.post<AuthResponse>(url, request).pipe(
      map(response => this.handleAuthSuccess(response)),
      catchError(error => {
        this.clearAuth();
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current user information
   */
  getCurrentUser(): Observable<UserInfo> {
    const url = `${this.baseUrl}/v1/auth/me`;
    return this.http.get<UserInfo>(url);
  }

  /**
   * Initiate forgot password process
   */
  forgotPassword(request: ForgotPasswordRequest): Observable<string> {
    const url = `${this.baseUrl}/v1/users/forgot-password`;
    return this.http.post(url, request, { responseType: 'text' }).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = 'An error occurred';

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          errorMessage = error.error.message;
        } else {
          // Server-side error - try to parse JSON error response
          try {
            if (typeof error.error === 'string') {
              // If error.error is a string, try to parse it as JSON
              const parsedError = JSON.parse(error.error);
              errorMessage = parsedError.message || parsedError.title || errorMessage;
            } else if (error.error?.message) {
              // If it's already an object with message
              errorMessage = error.error.message;
            } else if (error.status === 404) {
              errorMessage = 'User with this email address does not exist';
            } else if (error.status === 401) {
              errorMessage = 'Invalid credentials';
            } else if (error.status === 0) {
              errorMessage = 'Unable to connect to server';
            } else {
              errorMessage = `Server returned code ${error.status}`;
            }
          } catch (parseError) {
            // If JSON parsing fails, use default error messages
            if (error.status === 404) {
              errorMessage = 'User with this email address does not exist';
            } else if (error.status === 401) {
              errorMessage = 'Invalid credentials';
            } else if (error.status === 0) {
              errorMessage = 'Unable to connect to server';
            } else {
              errorMessage = `Server returned code ${error.status}`;
            }
          }
        }

        // Display error notification
        this.notification.notify(errorMessage, "error", 3000);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Reset password with OTP
   */
  resetPassword(request: ResetPasswordRequest): Observable<string> {
    const url = `${this.baseUrl}/v1/users/reset-password`;
    return this.http.post(url, request, { responseType: 'text' }).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = 'An error occurred';

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          errorMessage = error.error.message;
        } else {
          // Server-side error - try to parse JSON error response
          try {
            if (typeof error.error === 'string') {
              // If error.error is a string, try to parse it as JSON
              const parsedError = JSON.parse(error.error);
              errorMessage = parsedError.message || parsedError.title || errorMessage;
            } else if (error.error?.message) {
              // If it's already an object with message
              errorMessage = error.error.message;
            } else if (error.status === 400) {
              errorMessage = 'Invalid OTP or password';
            } else if (error.status === 404) {
              errorMessage = 'Invalid or expired OTP';
            } else if (error.status === 401) {
              errorMessage = 'Invalid credentials';
            } else if (error.status === 0) {
              errorMessage = 'Unable to connect to server';
            } else {
              errorMessage = `Server returned code ${error.status}`;
            }
          } catch (parseError) {
            // If JSON parsing fails, use default error messages
            if (error.status === 400) {
              errorMessage = 'Invalid OTP or password';
            } else if (error.status === 404) {
              errorMessage = 'Invalid or expired OTP';
            } else if (error.status === 401) {
              errorMessage = 'Invalid credentials';
            } else if (error.status === 0) {
              errorMessage = 'Unable to connect to server';
            } else {
              errorMessage = `Server returned code ${error.status}`;
            }
          }
        }

        // Display error notification
        this.notification.notify(errorMessage, "error", 3000);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const user = this.currentUserSubject.value;
    if (!user) return false;

    // Check if token is expired
    const now = new Date();
    return user.tokenExpiry > now;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.currentUserSubject.value;
    return user?.roles.includes(role) || false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.currentUserSubject.value;
    if (!user) return false;
    return roles.some(role => user.roles.includes(role));
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Returns `true` when the current user has the `admin` role **and** belongs
   * to the Management Team. Use this to gate destructive operations such as
   * entity archival — mirroring the backend `[RequireManagementTeamAdmin]` policy.
   */
  canArchive(): boolean {
    return this.isAdmin() && isManagementTeam(this.getCurrentUserValue());
  }

  /**
   * Get current user value
   */
  getCurrentUserValue(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get stored access token
   */
  getToken(): string | null {
    return this.localStorage.getItem<string>(this.TOKEN_KEY);
  }

  /**
   * Get stored refresh token
   */
  getStoredRefreshToken(): string | null {
    return this.localStorage.getItem<string>(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Handle successful authentication response
   */
  private handleAuthSuccess(response: AuthResponse): AuthUser {
    const tokenExpiry = new Date();
    tokenExpiry.setSeconds(tokenExpiry.getSeconds() + response.expiresIn);
    const currentUser = this.currentUserSubject.value;
    const resolvedTeamId = response.user.teamId
      ?? currentUser?.teamId
      ?? this.readLegacyLocalStorageValue('teamId');
    const resolvedTeam = response.user.team
      ?? currentUser?.team
      ?? this.readLegacyLocalStorageValue('team');

    const authUser: AuthUser = {
      id: response.user.id,
      name: response.user.name,
      loginId: response.user.loginId,
      roles: response.user.roles,
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenExpiry: tokenExpiry,
      teamId: resolvedTeamId,
      team: resolvedTeam

    };

    // Store in localStorage using StorageService
    this.localStorage
      .setItem(this.TOKEN_KEY, response.accessToken)
      .setItem(this.REFRESH_TOKEN_KEY, response.refreshToken)
      .setItem(this.USER_KEY, authUser);

    // Prime user preferences (server prefs win on conflict)
    this.userPrefsService.prime(authUser.id, response.user.preferences ?? {});

    // Update current user
    this.currentUserSubject.next(authUser);

    // Setup auto refresh
    this.setupAutoRefresh();

    return authUser;
  }

  /**
   * Load stored authentication data
   * Note: Only loads from localStorage - no HTTP calls to avoid circular dependency
   */
  private loadStoredAuth(): void {
    try {
      const authUser = this.localStorage.getItem<AuthUser>(this.USER_KEY);
      if (authUser) {
        authUser.tokenExpiry = new Date(authUser.tokenExpiry);
        authUser.teamId = authUser.teamId ?? this.readLegacyLocalStorageValue('teamId');
        authUser.team = authUser.team ?? this.readLegacyLocalStorageValue('team');

        // Only load valid tokens - expired tokens are handled by interceptor
        if (this.isTokenValid(authUser.tokenExpiry)) {
          this.currentUserSubject.next(authUser);
          // Re-prime UserPrefsService so components work after a page reload.
          // No server prefs here — local prefs already live in localStorage.
          this.userPrefsService.prime(authUser.id);
        } else {
          // Clear expired auth - don't try to refresh here
          // The interceptor will handle token refresh on the first API call
          this.clearAuthData();
        }
      }
    } catch (error) {
      this.logger.e('Error loading stored auth:', error);
      this.clearAuthData();
    }
  }

  /**
   * Clear authentication data without navigation
   * Used internally to avoid side effects during initialization
   */
  private clearAuthData(): void {
    // Get current user ID before clearing for SignalR disconnect
    const currentUser = this.getCurrentUserValue();
    const userId = currentUser?.id;

    // Disconnect from SignalR before clearing storage
    this.notificationsService.disconnect(userId).catch(error => {
      this.logger.e('Error disconnecting from SignalR during logout:', error);
    });

    // Clear all localStorage using StorageService
    this.localStorage.clear();

    // Reset user preferences state
    this.userPrefsService.clear();

    this.currentUserSubject.next(null);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Clear authentication data and redirect to login
   */
  private clearAuth(): void {
    this.clearAuthData();

    // Only redirect to login if not already on an auth-related page
    const currentUrl = this.router.url;
    const authPages = ['/login', '/forgot-password', '/reset-password', '/signUp'];
    const isOnAuthPage = authPages.some(page => currentUrl.startsWith(page));

    if (!isOnAuthPage) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Setup automatic token refresh
   */
  private setupAutoRefresh(): void {
    const user = this.currentUserSubject.value;
    if (!user) return;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Refresh token 5 minutes before expiry
    const refreshTime = user.tokenExpiry.getTime() - Date.now() - (5 * 60 * 1000);

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken().subscribe({
          error: (error) => {
            this.logger.e('Auto refresh failed:', error);
            this.clearAuth();
          }
        });
      }, refreshTime);
    }
  }

  /**
   * Reads legacy plain-string localStorage values written outside StorageService.
   * Supports both raw and JSON-stringified formats.
   */
  private readLegacyLocalStorageValue(key: string): string | undefined {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return undefined;
    }

    const normalized = raw.startsWith('"') && raw.endsWith('"')
      ? raw.slice(1, -1)
      : raw;

    return normalized || undefined;
  }

  /**
   * Check if token is valid (not expired)
   */
  private isTokenValid(expiry: Date): boolean {
    const now = new Date();
    return expiry > now;
  }

  /**
   * Handle HTTP errors
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    this.logger.e(`Initial error: `, error);
    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      if (error.error?.message) {
        // Prioritize server's error message
        errorMessage = error.error.message;
      } else if (error.status === 401) {
        errorMessage = 'Invalid credentials';
      } else if (error.status === 0) {
        errorMessage = 'Unable to connect to server';
      } else {
        errorMessage = `Server returned code ${error.status}`;
      }
    }

    // Display error notification
    this.notification.notify(errorMessage, "error", 3000);

    return throwError(() => new Error(errorMessage));
  }
}
